/**
 * ForceTraceChart Component
 *
 * Simplified, scoreboard-themed force-time chart using Recharts.
 * Display-only — no toggles, legend editing, or PDF export.
 */

import isNumber from 'lodash/isNumber';
import * as React from 'react';
import { Box } from '@chakra-ui/react';
import { AutoSizer } from 'react-virtualized';
import {
  CartesianGrid,
  Label,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  XAxis,
  YAxis,
} from 'recharts';
import { IGraph, ISize, ITest, ITimingEvent } from '../../../../data/types';
import { roundValue } from '../../../../utils/math';

interface IGraphData {
  Combined: number;
  Left: number;
  Right: number;
  Time: number;
  Hertz: number;
}

export interface ForceTraceChartProps {
  test: ITest;
  graphData: IGraphData[];
  graphRaw: IGraph;
  timingEvents: ITimingEvent[];
  dataMin: number;
  loading: boolean;
}

const timeTickFormatterFactory = (hertz: number): ((t: string) => string) => {
  return (tick: string): string => `${roundValue(+tick / hertz, 2)}s`;
};

export const ForceTraceChart: React.FC<ForceTraceChartProps> = ({
  test,
  graphData,
  graphRaw,
  timingEvents,
  loading,
}): React.ReactElement => {
  const isDualPlate = React.useMemo((): boolean => {
    return isNumber(graphData?.[0]?.Left);
  }, [graphData]);

  const getSystemWeight = React.useCallback((): number | undefined => {
    const { testType, weight }: Partial<ITest> = test ?? {};
    if (!testType) return undefined;
    if (testType.repetitions === true) {
      if (graphRaw?.systemWeight) {
        return graphRaw.systemWeight;
      }
    } else {
      return typeof weight === 'object' && weight !== null
        ? weight.value
        : (weight as number | undefined);
    }
  }, [test, graphRaw]);

  if (loading) {
    return (
      <Box flex={1} display='flex' alignItems='center' justifyContent='center'>
        <Box
          color='var(--scoreboard-text-secondary, #b0b0b0)'
          fontSize='18px'
          fontWeight={500}
        >
          Loading force data...
        </Box>
      </Box>
    );
  }

  if (!graphData || graphData.length === 0) {
    return (
      <Box flex={1} display='flex' alignItems='center' justifyContent='center'>
        <Box
          color='var(--scoreboard-text-secondary, #b0b0b0)'
          fontSize='18px'
          fontWeight={500}
        >
          Waiting for test data...
        </Box>
      </Box>
    );
  }

  return (
    <Box flex={1} w='100%' minH='300px'>
      <AutoSizer>
        {(size: ISize): React.ReactElement => (
          <LineChart
            height={size.height}
            width={size.width}
            data={graphData}
            margin={{ top: 10, right: 20, left: 10, bottom: 45 }}
          >
            <CartesianGrid stroke='rgba(255,255,255,.15)' strokeWidth={0.25} />

            {/* Phase regions */}
            {timingEvents?.map(
              (evt: ITimingEvent, i: number): React.ReactElement => (
                <ReferenceArea
                  key={i}
                  yAxisId='left'
                  x1={evt.start}
                  x2={evt.end}
                  ifOverflow='extendDomain'
                  stroke={evt.color}
                  fill={evt.color}
                  fillOpacity={0.2}
                  strokeOpacity={0}
                />
              )
            )}

            {/* System weight reference line */}
            {getSystemWeight() && (
              <ReferenceLine
                yAxisId='left'
                y={getSystemWeight()}
                ifOverflow='extendDomain'
                stroke='white'
                strokeWidth={0.5}
                strokeOpacity={0.5}
                label={{
                  value:
                    graphRaw?.plateModel === 'HDTS'
                      ? 'Pretension'
                      : 'System Weight',
                  position: 'insideTopRight',
                  fill: 'white',
                  fillOpacity: 0.5,
                }}
              />
            )}

            <XAxis
              dataKey='Time'
              axisLine={false}
              tickLine={false}
              tickFormatter={timeTickFormatterFactory(graphRaw?.hertz ?? 1000)}
              type='number'
              domain={['dataMin', 'dataMax']}
              tick={{
                fill: 'var(--scoreboard-text-secondary, #b0b0b0)',
                fontSize: 18,
              }}
              tickCount={Math.floor((graphRaw?.hertz ?? 1000) / 10)}
              xAxisId={0}
            >
              <Label
                position='bottom'
                offset={0}
                value='Time (s)'
                style={{
                  fill: 'var(--scoreboard-text-secondary, #b0b0b0)',
                  fontSize: 20,
                }}
              />
            </XAxis>

            <YAxis
              dataKey='Combined'
              domain={[0, (dataMax: number): number => dataMax]}
              yAxisId='left'
              axisLine={false}
              tickLine={false}
              allowDataOverflow
              tick={{
                fill: 'var(--scoreboard-text-secondary, #b0b0b0)',
                fontSize: 18,
              }}
              type='number'
              tickCount={100}
              tickFormatter={(tick: string): string =>
                `${roundValue(+tick, 0)}`
              }
            >
              <Label
                position='left'
                offset={0}
                angle={-90}
                value='Newtons'
                style={{
                  fill: 'var(--scoreboard-text-secondary, #b0b0b0)',
                  fontSize: 20,
                }}
              />
            </YAxis>

            {/* Force lines */}
            {isDualPlate && (
              <Line
                dataKey='Right'
                dot={false}
                isAnimationActive={false}
                yAxisId='left'
                stroke='#ff9800'
                strokeWidth={2.5}
              />
            )}
            {isDualPlate && (
              <Line
                dataKey='Left'
                dot={false}
                isAnimationActive={false}
                yAxisId='left'
                stroke='#00bcd4'
                strokeWidth={2.5}
              />
            )}
            <Line
              dataKey='Combined'
              dot={false}
              isAnimationActive={false}
              yAxisId='left'
              stroke='#ffffff'
              strokeWidth={2.5}
            />
          </LineChart>
        )}
      </AutoSizer>
    </Box>
  );
};
